"use client";
/**
 * COMANDA DIN ADRESĂ — puntea dintre paleta de comenzi și ecranul care execută.
 *
 * Paleta nu deschide ea dialoguri și nu duplică niciun formular: navighează la ecranul
 * care le are deja, cu o instrucțiune în adresă. Ecranul o citește la sosire:
 *
 *   `?nou=factura`   → deschide dialogul de creare (aceeași stare pe care o pune butonul lui)
 *   `?cauta=Bosch`   → pornește cu grila filtrată pe textul ăsta
 *   `?client=<id>`   → contextul: clientul de pe ecranul de unde a fost pornită acțiunea
 *
 * De ce prin adresă și nu printr-un context global: dialogul aparține ecranului, cu starea
 * lui, cu drepturile lui, cu `canWrite`-ul lui. O stare globală ar fi fost o a doua cale de
 * a deschide același formular — adică al doilea loc în care se strică.
 *
 * Parametrul se CONSUMĂ: după ce ecranul l-a primit, adresa se curăță (`replace`, fără intrare
 * nouă în istoric). Altfel „înapoi” ar redeschide dialogul, iar un refresh l-ar redeschide la
 * nesfârșit.
 */
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { PARAM_DESCHIDE } from "@/lib/quick-actions";

/** Parametrul care pre-completează căutarea grilei. */
export const PARAM_CAUTA = "cauta";
/**
 * CONTEXTUL cu care sosește acțiunea: clientul de pe ecranul de unde a fost pornită.
 *
 * „Factură nouă” apăsat pe fișa unui client trebuie să vină cu clientul completat — altfel
 * scurtătura economisește un clic și cere înapoi trei. Numele călătorește lângă id ca
 * ETICHETĂ, ca formularul să-l poată afișa fără încă o interogare; id-ul rămâne singurul
 * lucru care contează, iar serverul îl verifică oricum la salvare.
 */
export const PARAM_CLIENT = "client";
export const PARAM_CLIENT_NUME = "clientNume";

/**
 * Citește instrucțiunea din adresă O SINGURĂ DATĂ și o șterge din bară.
 *
 * `deschide` = cheia acțiunii rapide cerute (`"factura"`, `"client"`…), `cauta` = textul cu
 * care trebuie să pornească grila, `client` = clientul din context. Toate `null` când adresa
 * n-are nimic de spus.
 */
export type ComandaDinUrl = {
  deschide: string | null;
  cauta: string | null;
  /** clientul de pe ecranul de unde s-a pornit acțiunea, dacă a fost unul */
  client: { id: string; name: string } | null;
};

export function useComandaDinUrl(): ComandaDinUrl {
  const sp = useSearchParams();
  const pathname = usePathname();
  /* `client` e CONTEXTUL unei acțiuni din paletă doar lângă `?nou=…` (paleta îl trimite numai așa).
     Singur, `?client=<id>` e un FILTRU de listă citit pe server (ex. /facturare?client=…) — nu se
     consumă și nu se șterge din adresă, ca filtrul să rămână la refresh și să se poată scoate. */
  const idClient = sp.get(PARAM_DESCHIDE) ? sp.get(PARAM_CLIENT) : null;
  const dinUrl: ComandaDinUrl = {
    deschide: sp.get(PARAM_DESCHIDE),
    cauta: sp.get(PARAM_CAUTA),
    client: idClient ? { id: idClient, name: sp.get(PARAM_CLIENT_NUME) ?? "" } : null,
  };
  /* Se reține la prima randare, fiindcă imediat după aceea parametrul dispare din adresă:
     dacă am citi mereu din `sp`, valoarea s-ar face `null` sub ecran, iar un dialog care
     depinde de ea s-ar închide singur. */
  const [comanda] = useState(dinUrl);
  const curatat = useRef(false);

  useEffect(() => {
    if (curatat.current) return;
    /* `?cauta=` RĂMÂNE în adresă: e un filtru citit și pe server (/facturare, /vanzari), deci un
       refresh sau un link copiat trebuie să-l păstreze; se scoate din „×”-ul filtrului (11.10.2026). */
    if (!comanda.deschide && !comanda.client) return;
    curatat.current = true;
    const rest = new URLSearchParams(sp.toString());
    rest.delete(PARAM_DESCHIDE);
    rest.delete(PARAM_CLIENT);
    rest.delete(PARAM_CLIENT_NUME);
    const qs = rest.toString();
    /* `history.replaceState`, NU `router.replace`: parametrul și-a făcut treaba în client, iar
       ștergerea lui e cosmetică. Prin router, Next reface randarea de server a rutei — pe dev
       asta a însemnat secunde de recompilare cu dialogul deja deschis peste ea, iar pe producție
       o cerere în plus la fiecare acțiune pornită din paletă. Adresa se curăță pe loc, fără să
       clintească pagina. */
    window.history.replaceState(null, "", qs ? `${pathname}?${qs}` : pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return comanda;
}

/**
 * Varianta scurtă: „dialogul meu trebuie deschis?”. Cheamă `deschide()` o dată, la sosire.
 *
 * ```tsx
 * useDeschideDinUrl("factura", () => setDialog("invoice"));
 * ```
 */
export function useDeschideDinUrl(cheie: string, deschide: () => void): void {
  const { deschide: cerut } = useComandaDinUrl();
  const facut = useRef(false);
  useEffect(() => {
    if (facut.current || cerut !== cheie) return;
    facut.current = true;
    deschide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cerut]);
}
